import moment from 'moment';
import React, { useState } from 'react';

import { ABTestMode } from '~/src/dash/api/ab-test';
import { EBool } from '~/src/dash/api/types/bool';
import { Row, Rows, Text, TextButton } from '~/src/dash/publicaUI';
import { formatDateWithOffsetToUTC, toStartDate } from '~/src/dash/utils/date';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { IChannelABTest } from '~/src/dash/views/channels/api';
import { ChannelABTest } from '~/src/dash/views/channels/ChannelABTests/ABTest';

interface IChannelABTests {
  pastABTests: IChannelABTest[] | null;
  currABTest: IChannelABTest | null;
  createTest: (test: IChannelABTest, callback: () => void) => void;
  endTest: (test: IChannelABTest, callback: () => void) => void;
  implementTestParams: (test: IChannelABTest) => void;
}

export const ChannelABTests = ({
  currABTest,
  pastABTests,
  createTest,
  endTest,
  implementTestParams,
}: IChannelABTests) => {
  const { session, user } = useBaseStore();
  const writeAuthorization = IS_AUTHORIZED(['write_bidder'], user.user);
  const [newTest, setNewTest] = useState<IChannelABTest | null>(null);

  const generateDefaultTest = () => {
    const today = formatDateWithOffsetToUTC(toStartDate(moment()), session.publisher.Timezone);
    return {
      ID: 0,
      SiteID: session.site?.ID,
      Name: 'New Test Name',
      Active: 2,
      StartTime: today,
      EndTime: today,
      Timezone: session.publisher.Timezone,
      TrafficShareTestA: 50,
      TrafficShareTestB: 50,
      UseSyntheticIDTestA: EBool.EBool_FALSE,
      UseSyntheticIDTestB: EBool.EBool_FALSE,
    } as IChannelABTest;
  };

  const handleOnCreateTest = (test: IChannelABTest) => {
    createTest(test, () => setNewTest(null));
  };

  const handleOnEndTest = (test: IChannelABTest) => {
    endTest(test, () => setNewTest(null));
  };

  return (
    <Rows>
      <Row paddingBottom='0'>
        <Text subheader>Current Test</Text>
      </Row>
      <Row>
        {currABTest ? (
          <ChannelABTest
            test={currABTest}
            onEndTest={handleOnEndTest}
            testType={ABTestMode.Current}
          />
        ) : (
          <Text small muted>
            {newTest ? (
              <ChannelABTest
                onCreateTest={handleOnCreateTest}
                onCancelTest={() => setNewTest(null)}
                onChange={(test: IChannelABTest) => setNewTest(test)}
                test={newTest}
                testType={ABTestMode.New}
              />
            ) : (
              <TextButton
                onClick={() => setNewTest(generateDefaultTest())}
                disabled={!writeAuthorization.isAuthorized}
              >
                + New A/B Test
              </TextButton>
            )}
          </Text>
        )}
      </Row>

      <Row paddingBottom='0'>
        <Text subheader>Previous Tests</Text>
      </Row>
      {pastABTests && pastABTests.length > 0 ? (
        pastABTests.map((test: IChannelABTest) => (
          <Row paddingBottom='0'>
            <ChannelABTest
              test={test}
              testType={ABTestMode.Past}
              onImplementTestParams={implementTestParams}
            />
          </Row>
        ))
      ) : (
        <Row paddingBottom='0'>
          <Text small muted>
            None
          </Text>
        </Row>
      )}
    </Rows>
  );
};
